import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { Observable, firstValueFrom, map } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  UserNotification,
  UserNotificationsResponse,
} from '../../models/client/notification.model';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private http = inject(HttpClient);

  private BASE_URL = environment.CLIENT_BASE_URL + '/v1/notifications';
  private messaging = getMessaging(initializeApp(environment.firebase));

  // Opt-in entry point (notifications modal): prompts the user for permission,
  // then registers the token. Use this when the user explicitly enables
  // notifications.
  async requestPermissionAndRegister(): Promise<void> {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission denied');
        return;
      }
      await this.registerToken();
    } catch (error) {
      console.error('Error registering push notifications:', error);
    }
  }

  // Silent refresh (called on every authenticated app load): re-fetches and
  // re-POSTs the current token only if the user has already granted
  // permission, so returning users stay fresh without ever being re-prompted.
  async refreshTokenIfGranted(): Promise<void> {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return;
    }
    try {
      await this.registerToken();
    } catch (error) {
      console.error('Error refreshing push token:', error);
    }
  }

  // Handle foreground notifications
  onForegroundMessage(callback: (payload: any) => void): void {
    onMessage(this.messaging, callback);
  }

  // The logged-in user's notifications feed. The server scopes results to this
  // user's delivered broadcasts (newest first)
  getMyNotifications(): Observable<UserNotification[]> {
    return this.http
      .get<any>(`${this.BASE_URL}/get-notifications`)
      .pipe(
        map((res) => {
          const data = res?.data;
          if (Array.isArray(data)) return data as UserNotification[];
          if (data && Array.isArray((data as any).data)) return (data as any).data as UserNotification[];
          return [] as UserNotification[];
        })
      );
  }

  private async registerToken(): Promise<void> {
    // Pass the active environment's Firebase config to the service worker via
    // its registration URL.
    const swUrl = `/firebase-messaging-sw.js?firebaseConfig=${encodeURIComponent(
      JSON.stringify(environment.firebase)
    )}`;
    const serviceWorkerRegistration =
      await navigator.serviceWorker.register(swUrl);

    const token = await getToken(this.messaging, {
      vapidKey: environment.firebase.vapidKey,
      serviceWorkerRegistration,
    });

    if (token) {
      await firstValueFrom(this.saveFMCToken(token));
      console.log('FCM token registered');
    }
  }

  private saveFMCToken(token: string) {
    return this.http.post(`${this.BASE_URL}/create-fmc-token`, {
      FMC_token: token,
      platform: 'WEB',
    });
  }

  getAdImages(): Observable<{ id: string; url: string; created_at: string }[]> {
    return this.http.get<any>(`${this.BASE_URL}/get-ads`).pipe(
      map((res) => {
        const data = res?.data;
        if (Array.isArray(data)) return data;
        return [];
      })
    );
  }

  openAll(): Observable<void> {
    return this.http.post(`${this.BASE_URL}/open-all`, {}).pipe(map(() => undefined));
  }
  
}
